/**
 * Compliance Dashboard Page — F007
 *
 * Directors-only compliance overview dashboard with:
 * - 4 status cards in a 2x2 responsive grid (Complaints, Calendar, ACC, File Compliance)
 * - Traffic light badges per card (green/yellow/orange/red)
 * - Count metrics and ACC progress bar
 * - Click-to-navigate on cards
 * - Summary text: "All compliance areas current" or "N areas require attention"
 * - Directors-only informational label
 *
 * @sprint SPRINT-21
 * @feature F007
 */

import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  Calendar,
  CheckSquare,
  FileText,
  Shield,
  Users,
  Award,
  Lock,
} from "lucide-react";
import { ComplianceLayout } from "./compliance-layout";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TrafficLight = "green" | "yellow" | "orange" | "red" | null;

interface ComplaintSection {
  active: number;
  overdue: number;
  status: TrafficLight;
}

interface CalendarSection {
  upcoming30Days: number;
  overdue: number;
  status: TrafficLight;
}

interface AccStatusSection {
  year: number | null;
  completionPercentage: number;
  status: TrafficLight;
}

interface FileComplianceSection {
  totalDeals: number;
  compliantDeals: number;
  completionPercentage: number;
  status: TrafficLight;
}

interface OperationsHubData {
  creditReps: {
    total: number;
    active: number;
    documentsExpiringSoon: number;
  };
  insurance: {
    totalActive: number;
    piAlertLevel: string | null;
    piDaysUntilExpiry: number | null;
  };
  documents: {
    total: number;
    expiring: number;
  };
  memberships: {
    total: number;
    expiringSoon: number;
  };
  security: {
    latestAuditCompliance: number | null;
    offshoreStaffCompliant: number;
    offshoreStaffTotal: number;
  };
}

interface DashboardData {
  complaints: ComplaintSection;
  calendar: CalendarSection;
  accStatus: AccStatusSection;
  fileCompliance: FileComplianceSection;
  operationsHub?: OperationsHubData;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TRAFFIC_LIGHT_CONFIG: Record<
  NonNullable<TrafficLight>,
  { label: string; className: string; dotClass: string }
> = {
  green: {
    label: "Current",
    className: "bg-green-100 text-green-700 border-green-300",
    dotClass: "bg-green-500",
  },
  yellow: {
    label: "Warning",
    className: "bg-yellow-100 text-yellow-700 border-yellow-300",
    dotClass: "bg-yellow-500",
  },
  orange: {
    label: "Attention",
    className: "bg-orange-100 text-orange-700 border-orange-300",
    dotClass: "bg-orange-500",
  },
  red: {
    label: "Critical",
    className: "bg-red-100 text-red-700 border-red-300",
    dotClass: "bg-red-500",
  },
};

function TrafficLightBadge({
  status,
  testId,
}: {
  status: TrafficLight;
  testId: string;
}) {
  if (!status) {
    return (
      <Badge
        variant="outline"
        className="text-xs bg-slate-100 text-slate-500 border-slate-200"
        data-testid={testId}
      >
        No Data
      </Badge>
    );
  }

  const cfg = TRAFFIC_LIGHT_CONFIG[status];
  return (
    <Badge
      variant="outline"
      className={`text-xs ${cfg.className}`}
      data-testid={testId}
    >
      <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Status Card
// ---------------------------------------------------------------------------

interface StatusCardProps {
  testId: string;
  title: string;
  icon: React.ReactNode;
  status: TrafficLight;
  trafficLightTestId: string;
  isLoading: boolean;
  href: string;
  children: React.ReactNode;
}

function StatusCard({
  testId,
  title,
  icon,
  status,
  trafficLightTestId,
  isLoading,
  href,
  children,
}: StatusCardProps) {
  const [, navigate] = useLocation();

  return (
    <Card
      data-testid={testId}
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={() => navigate(href)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <span className="text-muted-foreground">{icon}</span>
          {title}
        </CardTitle>
        {isLoading ? (
          <Skeleton className="h-5 w-16" />
        ) : (
          <TrafficLightBadge status={status} testId={trafficLightTestId} />
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ComplianceDashboardPage() {
  const { data: dashboard, isLoading } = useQuery<DashboardData>({
    queryKey: ["/api/compliance/dashboard"],
  });

  // Compute how many areas need attention
  const areasNeedingAttention = dashboard
    ? [
        dashboard.complaints.status,
        dashboard.calendar.status,
        dashboard.accStatus.status,
        dashboard.fileCompliance.status,
      ].filter((s) => s !== null && s !== "green").length
    : 0;

  const allGreen =
    dashboard !== undefined &&
    areasNeedingAttention === 0 &&
    [
      dashboard.complaints.status,
      dashboard.calendar.status,
      dashboard.fileCompliance.status,
    ].every((s) => s === "green");

  const summaryText = isLoading
    ? null
    : allGreen
    ? "All compliance areas current"
    : `${areasNeedingAttention} area${areasNeedingAttention === 1 ? "" : "s"} require${areasNeedingAttention === 1 ? "s" : ""} attention`;

  return (
    <ComplianceLayout>
      {/* ------------------------------------------------------------------ */}
      {/* Header: title + directors label                                     */}
      {/* ------------------------------------------------------------------ */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Compliance Dashboard
          </h2>
          {!isLoading && (
            <p
              className={`mt-1 text-sm ${allGreen ? "text-green-600" : "text-orange-600"}`}
              data-testid="dashboard-summary-text"
            >
              {summaryText}
            </p>
          )}
          {isLoading && (
            <Skeleton
              className="mt-1 h-4 w-48"
              data-testid="dashboard-summary-text"
            />
          )}
        </div>
        <Badge
          variant="outline"
          className="text-xs bg-purple-50 text-purple-700 border-purple-300"
          data-testid="label-directors-only"
        >
          <Shield className="mr-1 h-3 w-3" />
          Directors Overview
        </Badge>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* 2x2 Status Card Grid                                                */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* ---- Card 1: Complaints ---- */}
        <StatusCard
          testId="card-complaints"
          title="Complaints"
          icon={<AlertTriangle className="h-4 w-4" />}
          status={dashboard?.complaints.status ?? null}
          trafficLightTestId="traffic-light-complaints"
          isLoading={isLoading}
          href="/compliance/complaints"
        >
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span
                className="text-2xl font-bold"
                data-testid="metric-complaints-active"
              >
                {dashboard?.complaints.active ?? 0}
              </span>
              <span className="text-sm text-muted-foreground">active</span>
            </div>
            <p
              className={`text-sm ${
                (dashboard?.complaints.overdue ?? 0) > 0
                  ? "text-red-600"
                  : "text-muted-foreground"
              }`}
              data-testid="metric-complaints-overdue"
            >
              {dashboard?.complaints.overdue ?? 0} overdue
              acknowledgement
              {(dashboard?.complaints.overdue ?? 0) === 1 ? "" : "s"}
            </p>
          </div>
        </StatusCard>

        {/* ---- Card 2: Calendar & Deadlines ---- */}
        <StatusCard
          testId="card-calendar"
          title="Calendar & Deadlines"
          icon={<Calendar className="h-4 w-4" />}
          status={dashboard?.calendar.status ?? null}
          trafficLightTestId="traffic-light-calendar"
          isLoading={isLoading}
          href="/compliance/calendar"
        >
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span
                className="text-2xl font-bold"
                data-testid="metric-calendar-upcoming"
              >
                {dashboard?.calendar.upcoming30Days ?? 0}
              </span>
              <span className="text-sm text-muted-foreground">
                upcoming (30 days)
              </span>
            </div>
            <p
              className={`text-sm ${
                (dashboard?.calendar.overdue ?? 0) > 0
                  ? "text-red-600"
                  : "text-muted-foreground"
              }`}
              data-testid="metric-calendar-overdue"
            >
              {dashboard?.calendar.overdue ?? 0} overdue event
              {(dashboard?.calendar.overdue ?? 0) === 1 ? "" : "s"}
            </p>
          </div>
        </StatusCard>

        {/* ---- Card 3: ACC Status ---- */}
        <StatusCard
          testId="card-acc-status"
          title="ACC Status"
          icon={<CheckSquare className="h-4 w-4" />}
          status={dashboard?.accStatus.status ?? null}
          trafficLightTestId="traffic-light-acc"
          isLoading={isLoading}
          href="/compliance/calendar"
        >
          {dashboard?.accStatus.year == null ? (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                No ACC checklist generated yet
              </p>
              <p className="text-xs text-muted-foreground">
                Go to Calendar to generate this year&apos;s ACC
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold">
                  {dashboard.accStatus.completionPercentage}%
                </span>
                <span className="text-sm text-muted-foreground">
                  complete ({dashboard.accStatus.year})
                </span>
              </div>
              <Progress
                value={dashboard.accStatus.completionPercentage}
                className="h-2"
                data-testid="acc-progress-bar"
              />
            </div>
          )}
        </StatusCard>

        {/* ---- Card 4: File Compliance ---- */}
        <StatusCard
          testId="card-file-compliance"
          title="File Compliance"
          icon={<FileText className="h-4 w-4" />}
          status={dashboard?.fileCompliance.status ?? null}
          trafficLightTestId="traffic-light-file-compliance"
          isLoading={isLoading}
          href="/compliance/complaints"
        >
          <div className="space-y-1">
            <div className="flex items-baseline gap-2">
              <span
                className="text-2xl font-bold"
                data-testid="metric-file-compliant-deals"
              >
                {dashboard?.fileCompliance.compliantDeals ?? 0}
              </span>
              <span className="text-sm text-muted-foreground">
                of{" "}
                <span data-testid="metric-file-total-deals">
                  {dashboard?.fileCompliance.totalDeals ?? 0}
                </span>{" "}
                deals compliant
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {dashboard?.fileCompliance.completionPercentage ?? 100}% compliance
              rate
            </p>
          </div>
        </StatusCard>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Operations Hub Section                                               */}
      {/* ------------------------------------------------------------------ */}
      {dashboard?.operationsHub && (
        <>
          <div className="mt-8 mb-4">
            <h3 className="text-lg font-semibold tracking-tight">
              Operations Hub
            </h3>
            <p className="text-sm text-muted-foreground">
              Credit representatives, insurance, documents, memberships &amp; security
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* ---- CR Compliance ---- */}
            <StatusCard
              testId="card-credit-reps"
              title="Credit Representatives"
              icon={<Users className="h-4 w-4" />}
              status={
                dashboard.operationsHub.creditReps.documentsExpiringSoon > 0
                  ? "yellow"
                  : dashboard.operationsHub.creditReps.active > 0
                    ? "green"
                    : null
              }
              trafficLightTestId="traffic-light-credit-reps"
              isLoading={false}
              href="/compliance/credit-reps"
            >
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" data-testid="metric-cr-active">
                    {dashboard.operationsHub.creditReps.active}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    of {dashboard.operationsHub.creditReps.total} active
                  </span>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="metric-cr-docs-expiring">
                  {dashboard.operationsHub.creditReps.documentsExpiringSoon} document{dashboard.operationsHub.creditReps.documentsExpiringSoon === 1 ? "" : "s"} expiring soon
                </p>
              </div>
            </StatusCard>

            {/* ---- Insurance ---- */}
            <StatusCard
              testId="card-insurance"
              title="Insurance Policies"
              icon={<Shield className="h-4 w-4" />}
              status={
                dashboard.operationsHub.insurance.piAlertLevel === "blocking" || dashboard.operationsHub.insurance.piAlertLevel === "critical"
                  ? "red"
                  : dashboard.operationsHub.insurance.piAlertLevel === "urgent"
                    ? "orange"
                    : dashboard.operationsHub.insurance.piAlertLevel === "warning"
                      ? "yellow"
                      : dashboard.operationsHub.insurance.totalActive > 0
                        ? "green"
                        : null
              }
              trafficLightTestId="traffic-light-insurance"
              isLoading={false}
              href="/compliance/insurance"
            >
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" data-testid="metric-insurance-active">
                    {dashboard.operationsHub.insurance.totalActive}
                  </span>
                  <span className="text-sm text-muted-foreground">active policies</span>
                </div>
                {dashboard.operationsHub.insurance.piDaysUntilExpiry !== null && (
                  <p
                    className={`text-sm ${
                      (dashboard.operationsHub.insurance.piDaysUntilExpiry ?? 999) <= 30
                        ? "text-red-600"
                        : "text-muted-foreground"
                    }`}
                    data-testid="metric-pi-expiry"
                  >
                    PI expires in {dashboard.operationsHub.insurance.piDaysUntilExpiry} day{dashboard.operationsHub.insurance.piDaysUntilExpiry === 1 ? "" : "s"}
                  </p>
                )}
              </div>
            </StatusCard>

            {/* ---- Document Library ---- */}
            <StatusCard
              testId="card-documents"
              title="Document Library"
              icon={<FileText className="h-4 w-4" />}
              status={
                dashboard.operationsHub.documents.expiring > 0
                  ? "yellow"
                  : dashboard.operationsHub.documents.total > 0
                    ? "green"
                    : null
              }
              trafficLightTestId="traffic-light-documents"
              isLoading={false}
              href="/compliance/documents"
            >
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" data-testid="metric-docs-total">
                    {dashboard.operationsHub.documents.total}
                  </span>
                  <span className="text-sm text-muted-foreground">total documents</span>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="metric-docs-expiring">
                  {dashboard.operationsHub.documents.expiring} expiring within 60 days
                </p>
              </div>
            </StatusCard>

            {/* ---- Memberships ---- */}
            <StatusCard
              testId="card-memberships"
              title="Memberships"
              icon={<Award className="h-4 w-4" />}
              status={
                dashboard.operationsHub.memberships.expiringSoon > 0
                  ? "yellow"
                  : dashboard.operationsHub.memberships.total > 0
                    ? "green"
                    : null
              }
              trafficLightTestId="traffic-light-memberships"
              isLoading={false}
              href="/compliance/memberships"
            >
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" data-testid="metric-memberships-total">
                    {dashboard.operationsHub.memberships.total}
                  </span>
                  <span className="text-sm text-muted-foreground">active memberships</span>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="metric-memberships-expiring">
                  {dashboard.operationsHub.memberships.expiringSoon} expiring within 30 days
                </p>
              </div>
            </StatusCard>

            {/* ---- Data Security ---- */}
            <StatusCard
              testId="card-security"
              title="Data Security"
              icon={<Lock className="h-4 w-4" />}
              status={
                dashboard.operationsHub.security.latestAuditCompliance !== null
                  ? dashboard.operationsHub.security.latestAuditCompliance >= 80
                    ? "green"
                    : dashboard.operationsHub.security.latestAuditCompliance >= 60
                      ? "yellow"
                      : "red"
                  : null
              }
              trafficLightTestId="traffic-light-security"
              isLoading={false}
              href="/compliance/security-audits"
            >
              <div className="space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold" data-testid="metric-audit-compliance">
                    {dashboard.operationsHub.security.latestAuditCompliance !== null
                      ? `${dashboard.operationsHub.security.latestAuditCompliance}%`
                      : "\u2014"}
                  </span>
                  <span className="text-sm text-muted-foreground">audit compliance</span>
                </div>
                <p className="text-sm text-muted-foreground" data-testid="metric-offshore-staff">
                  {dashboard.operationsHub.security.offshoreStaffCompliant}/{dashboard.operationsHub.security.offshoreStaffTotal} offshore staff compliant
                </p>
              </div>
            </StatusCard>
          </div>
        </>
      )}
    </ComplianceLayout>
  );
}
